import React from 'react';
import {
  Box, Container, Grid, Typography, Chip, Divider,
} from '@mui/material';
import PageHero from '../components/PageHero';
import ForestIcon from '@mui/icons-material/Forest';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const timeline = [
  { year: '2016', title: 'Fundación', desc: 'Un grupo de profesionales crea FENAGRO APL con la visión de transformar el agro colombiano mediante asociatividad empresarial agroindustrial.' },
  { year: '2017', title: 'Primeros Asociados', desc: 'Se incorporan las primeras redes empresariales de primer nivel, dando vida a la estructura colaborativa del sector solidario.' },
  { year: '2019', title: 'Banco de Proyectos', desc: 'Se formaliza el Banco de Proyectos Agroindustrial, conectando productores con fuentes de financiamiento y cofinanciación pública-privada.' },
  { year: '2021', title: 'Alianzas Estratégicas', desc: 'Consolidación de alianzas con Holdin Socioempresarial Solidario, ECOSO y MERCOSUR para ampliar el alcance internacional.' },
  { year: '2024', title: 'Expansión Nacional', desc: 'Crecimiento sostenido hacia nuevas regiones de Colombia, fortaleciendo la red de prosumidores y mercados asegurados.' },
];

export default function Nosotros() {
  return (
    <Box>
      <PageHero
        title="Nuestra Historia"
        subtitle="Desde 2016 transformando el campo colombiano con innovación, solidaridad y compromiso."
        breadcrumbs={[{ label: 'Nosotros' }]}
      />

      {/* Historia */}
      <Box sx={{ py: { xs: 8, md: 14 }, background: '#f8f5ef' }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 2, display: 'block' }}>
                ¿Por qué existimos?
              </Typography>
              <Typography variant="h3" sx={{ color: '#1a1a1a', mb: 3, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                Nacimos para resolver un problema real del campo
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 2 }}>
                La federación FENAGRO APL fue constituida para orientar e instruir a los asociados 
                en la solución de problemáticas socioeconómicas insatisfechas, encontrando su fortaleza 
                en la solidaridad y cooperación como pilar principal.
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 2 }}>
                El sector agropecuario colombiano carecía de educación rural pertinente, tecnificación, 
                mejoramiento genético e implementación de modelos asociativos agroindustriales exitosos. 
                La intermediación comercial absorbía el mayor porcentaje del precio de los productos 
                campesinos, dejando escasos ingresos para los productores.
              </Typography>
              <Typography variant="body1" sx={{ color: '#4a5568', mb: 4 }}>
                Frente a esta realidad, y sumado a décadas de conflictos bélicos, un grupo de profesionales 
                decidió crear un programa empresarial innovador que genere mejores ingresos a los campesinos, 
                garantice la seguridad alimentaria y promueva la producción orgánica.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {['Productores', 'Compradores', 'Comercializadores', 'Sector Solidario'].map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    variant="outlined"
                    sx={{ borderColor: '#1a4d2e', color: '#1a4d2e', fontWeight: 600 }}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Propósito boxes */}
              {[
                { icon: <ForestIcon />, title: 'Para los Productores', desc: 'Mayores ayudas que les permitan mejorar su productividad y obtener mejores ganancias por su actividad.' },
                { icon: <PeopleAltIcon />, title: 'Para los Compradores', desc: 'Variedad de productos orgánicos de mejor calidad, a mejores precios y más accesibles.' },
                { icon: <AccountTreeIcon />, title: 'Para los Comercializadores', desc: 'Precio justo en la intermediación y apertura de nuevos mercados orgánicos nacionales e internacionales.' },
              ].map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 3.5, mb: 2.5,
                    background: '#ffffff',
                    borderRadius: 3,
                    border: '1px solid rgba(26,77,46,0.1)',
                    display: 'flex', gap: 2, alignItems: 'flex-start',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(26,77,46,0.12)',
                      borderColor: 'rgba(26,77,46,0.25)',
                      transform: 'translateX(6px)',
                    },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: 2, flexShrink: 0,
                    background: 'linear-gradient(135deg, #1a4d2e, #2d7a4a)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    '& svg': { color: '#ffffff', fontSize: 22 },
                  }}>
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.8, color: '#1a1a1a' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096', lineHeight: 1.65 }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Timeline */}
      <Box sx={{ py: { xs: 8, md: 14 }, background: '#ffffff' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" sx={{ color: '#1a4d2e', mb: 1, display: 'block' }}>
              Nuestra Trayectoria
            </Typography>
            <Typography variant="h3" sx={{ color: '#1a1a1a', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              Hitos de Crecimiento
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            {/* Vertical line */}
            <Box sx={{
              position: 'absolute', left: { xs: 20, md: '50%' },
              top: 0, bottom: 0, width: 2,
              background: 'linear-gradient(to bottom, #c8a84b, #1a4d2e)',
              transform: { md: 'translateX(-50%)' },
            }} />

            {timeline.map((item, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', md: i % 2 === 0 ? 'row' : 'row-reverse' },
                  mb: 6, position: 'relative',
                  pl: { xs: 6, md: 0 },
                }}
              >
                {/* Dot */}
                <Box sx={{
                  position: 'absolute',
                  left: { xs: 14, md: 'calc(50% - 10px)' },
                  top: 8,
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #c8a84b, #e0c870)',
                  border: '3px solid #ffffff',
                  boxShadow: '0 0 0 3px #1a4d2e',
                  zIndex: 1,
                }} />

                <Box sx={{ width: { xs: '100%', md: '45%' }, mr: { md: i % 2 === 0 ? 0 : 'auto' }, ml: { md: i % 2 === 0 ? 'auto' : 0 } }}>
                  <Box sx={{
                    p: 3.5, borderRadius: 3,
                    background: '#f8f5ef',
                    border: '1px solid rgba(200,168,75,0.2)',
                    mr: { md: i % 2 === 0 ? 0 : 3 },
                    ml: { md: i % 2 === 0 ? 3 : 0 },
                  }}>
                    <Typography sx={{
                      fontFamily: "'Playfair Display', serif",
                      color: '#c8a84b', fontWeight: 700,
                      fontSize: '1.8rem', lineHeight: 1, mb: 1,
                    }}>
                      {item.year}
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1, color: '#1a1a1a', fontSize: '1rem' }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Propósito */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #1a4d2e, #0e2a18)',
        textAlign: 'center',
      }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: '#c8a84b', mb: 2, display: 'block' }}>
            Propósito de la Federación
          </Typography>
          <Typography variant="h3" sx={{
            color: '#ffffff', mb: 4,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            fontStyle: 'italic',
          }}>
            "Hacer del campo colombiano un espacio de oportunidades, prosperidad y dignidad"
          </Typography>
          <Divider sx={{ borderColor: 'rgba(200,168,75,0.3)', width: 80, mx: 'auto', mb: 4, borderBottomWidth: 2 }} />
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', maxWidth: 600, mx: 'auto' }}>
            Como entidad de economía solidaria de segundo nivel, propendemos por el bienestar de las empresas 
            asociadas mediante la defensa de sus derechos y la realización de programas que respondan a sus 
            necesidades bajo los principios de unidad, igualdad, respeto y solidaridad.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
